import React, { useRef, useState } from "react";
import { Button, Spinner } from "reactstrap";
import { RepositoryFactory } from "../../Repository/RepositoryFactory";

/*
    API Access (static#49) — the course picker, Phase C.

    Dave accepted a plain "type the course id" box. This is that box with a
    search in front of it: the picker is the happy path, the raw id is the
    escape hatch underneath, and they are the same field. Typing 7921 and
    pressing Search resolves the course and shows its name, so a customer who
    knows the id still gets confirmation of what they picked rather than
    committing a number they cannot check.

    THE CONTROL MATCHES THE PLAN'S SHAPE. A one-course plan gets radios, so
    choosing a second course replaces the first and over-selection is not
    expressible. A multi-course plan gets checkboxes, a running count of what is
    left, and unchecked boxes disabled once the cap is reached.

    THE CAP IS STILL NOT ENFORCED HERE. `maxCourses` comes from GET /api-access,
    which reads it from config/provisioning.js. The server re-derives the same
    rule on POST /api-access/provision and refuses anything that breaks it,
    because everything in this file can be edited by whoever has the browser
    open. This only makes the correct choice the easy one.

    AN UNKNOWN CAP DISABLES THE PICKER RATHER THAN GUESSING. `maxCourses` is
    null when the backend could not resolve the customer's plan — an api_plan
    that is NULL or has no rule in config/provisioning.js. Provisioning answers
    503 NOT_CONFIGURED for exactly those users, so letting them search, choose
    and press Provision only walks them into a failure. Three live usage plans
    are in this state today. Say so instead.
*/

const golfbert = RepositoryFactory.get("golfbert");

/** `{ data: { data: { resources: [...] } } }` — see HolesAction.js. */
const resourcesOf = (response) => response?.data?.data?.resources || [];

/** "Ironwood GC — Hopkins, MN (#7921)" */
export const courseLabel = (course) => {
  const where = [course?.city, course?.state].filter(Boolean).join(", ");
  const name = course?.name || "Unnamed course";
  return where
    ? `${name} — ${where} (#${course.id})`
    : `${name} (#${course.id})`;
};

const CoursePicker = ({ maxCourses, selected = [], onChange, disabled }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState(null);
  const [searched, setSearched] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const containerRef = useRef(null);

  // A cap we cannot read is not a cap of zero and not a cap of infinity — it is
  // an unanswered question, and the server will refuse the provision anyway.
  const cap =
    typeof maxCourses === "number" && maxCourses > 0 ? maxCourses : null;
  const planKnown = cap !== null;
  const singleChoice = cap === 1;
  const remaining = planKnown ? Math.max(cap - selected.length, 0) : 0;
  const atCapacity = planKnown && selected.length >= cap;
  const locked = disabled || !planKnown;

  const alreadyPicked = (id) => selected.some((course) => course.id === id);

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const term = query.trim();
    if (!term) return;

    setSearching(true);
    setMessage(null);
    setResults([]);
    try {
      // A bare number is a course id, not a course name. Resolve it directly —
      // this is Dave's version of the control, and it doubles as a check that
      // the id is real before the customer commits to it.
      if (/^\d+$/.test(term)) {
        const response = await golfbert.getCourseById(term);
        const course = response?.data?.data || response?.data;
        if (course && course.id) {
          setResults([course]);
        } else {
          setMessage(`No course has the id ${term}.`);
        }
      } else {
        setResults(
          resourcesOf(await golfbert.getCourses({ name: term, limit: 25 }))
        );
      }
      setResultsOpen(true);
    } catch (error) {
      // Never log the error object itself — see the note in apiAccessAction.js.
      setMessage("We couldn't search courses just now. Please try again.");
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  /*
      Collapse when focus leaves the picker entirely.

      A search can return 25 rows, and they used to stay on screen until the
      next search, pushing the Provision button off the bottom of the panel.
      React's onBlur bubbles, so this fires for any control inside; the
      relatedTarget check keeps the list open while focus is merely moving
      between rows within it.
  */
  const handleBlur = (event) => {
    const next = event.relatedTarget;
    if (next && containerRef.current && containerRef.current.contains(next)) {
      return;
    }
    setResultsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && resultsOpen) {
      setResultsOpen(false);
    }
  };

  const choose = (course) => {
    const entry = { id: Number(course.id), label: courseLabel(course) };

    if (singleChoice) {
      // Replace, never append. On a one-course plan a second choice is a change
      // of mind, so there is no capacity to hit and nothing to remove first.
      onChange([entry]);
      setResultsOpen(false);
      return;
    }

    if (alreadyPicked(entry.id)) {
      onChange(selected.filter((course) => course.id !== entry.id));
      return;
    }
    if (atCapacity) return;
    onChange([...selected, entry]);
  };

  const remove = (id) =>
    onChange(selected.filter((course) => course.id !== id));

  /** What the customer needs to know before they start clicking. */
  const guidance = () => {
    if (!planKnown) {
      return "We can't tell which courses your plan covers, so course selection is unavailable. Please contact apisupport@golfbert.com.";
    }
    if (singleChoice) {
      return selected.length === 0
        ? "Your plan covers 1 course. Choose the one you need."
        : "Your plan covers 1 course. Choosing another replaces it.";
    }
    return `Your plan covers ${cap} courses. You have chosen ${selected.length} — ${remaining} remaining.`;
  };

  return (
    <div
      className="course-picker mb-3"
      ref={containerRef}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <label className="d-block mb-1 fw-medium" htmlFor="course-picker-search">
        {singleChoice
          ? "Which course do you need access to?"
          : "Which courses do you need access to?"}
      </label>
      <p className={`small mb-2 ${planKnown ? "text-muted" : "text-danger"}`}>
        {guidance()}
      </p>

      <form onSubmit={runSearch}>
        <div className="input-group">
          <input
            id="course-picker-search"
            type="text"
            className="form-control"
            placeholder="Course name, city, or course id"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setResultsOpen(true)}
            disabled={locked}
          />
          <Button
            type="submit"
            size="sm"
            disabled={locked || searching || !query.trim()}
          >
            {searching ? <Spinner size="sm" className="mx-2" /> : "Search"}
          </Button>
        </div>
      </form>

      {message && <p className="text-muted small mt-2 mb-0">{message}</p>}

      {resultsOpen && results.length > 0 && (
        <ul
          className="list-unstyled border rounded mt-2 mb-0 course-picker-results"
          /* Never let the result list own the page. Bounded here rather than in
             a stylesheet so the constraint travels with the component. */
          style={{ maxHeight: "16rem", overflowY: "auto" }}
        >
          {results.map((course) => {
            const id = Number(course.id);
            const checked = alreadyPicked(id);
            // Only the unchosen rows go dead at the cap — a chosen one must stay
            // clickable so it can be unchosen.
            const rowDisabled =
              locked || (!singleChoice && atCapacity && !checked);
            return (
              <li key={course.id} className="border-bottom">
                <label
                  className={`d-flex align-items-center gap-2 px-3 py-2 mb-0 ${
                    rowDisabled ? "text-muted" : ""
                  }`}
                  style={{ cursor: rowDisabled ? "not-allowed" : "pointer" }}
                >
                  <input
                    type={singleChoice ? "radio" : "checkbox"}
                    name="course-picker-choice"
                    className="form-check-input mt-0 flex-shrink-0"
                    checked={checked}
                    disabled={rowDisabled}
                    onChange={() => choose(course)}
                  />
                  <span>{courseLabel(course)}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {searched && !searching && results.length === 0 && !message && (
        <p className="text-muted small mt-2 mb-0">
          No courses matched that. Try a shorter name, or the course id.
        </p>
      )}

      {selected.length > 0 && (
        <div className="mt-3">
          <p className="mb-1 small fw-medium">
            {singleChoice ? "Chosen course:" : "Chosen courses:"}
          </p>
          <ul className="list-unstyled mb-0">
            {selected.map((course) => (
              <li
                key={course.id}
                className="d-flex justify-content-between align-items-center py-1"
              >
                <span className="small">{course.label}</span>
                <Button
                  type="button"
                  size="sm"
                  color="link"
                  className="p-0"
                  disabled={disabled}
                  onClick={() => remove(course.id)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {atCapacity && !singleChoice && (
        <p className="text-muted small mt-2 mb-0">
          That's everything your plan covers. Remove one to choose a different
          course, or change your plan to add more.
        </p>
      )}
    </div>
  );
};

export default CoursePicker;
